import type { Component } from 'vue';
import {
  isLauncherTheme,
  type LauncherTheme,
} from '@features/tenant/launcherTheme';

export const THEMED_SECTIONS = [
  'rankings',
  'missions',
  'events',
  'home',
  'rewards',
  'panel',
  'plans',
] as const;

export type ThemedSection = (typeof THEMED_SECTIONS)[number];

type SectionLoader = () => Promise<Component | { default: Component }>;

type SectionRegistry = Record<
  ThemedSection,
  Partial<Record<LauncherTheme, SectionLoader>> & { minimal: SectionLoader }
>;

export const SECTION_VIEWS: SectionRegistry = {
  rankings: {
    minimal: () => import('@features/rankings/views/RankingsView.vue'),
    arena: () => import('@features/rankings/views/ArenaRankingsView.vue'),
    modern: () => import('@features/rankings/views/ModernRankingsView.vue'),
  },
  missions: {
    minimal: () => import('@features/missions/views/MissionsView.vue'),
    arena: () => import('@features/missions/views/ArenaMissionsView.vue'),
    modern: () => import('@features/missions/views/ModernMissionsView.vue'),
  },
  events: {
    minimal: () => import('@features/events/views/EventView.vue'),
    arena: () => import('@features/events/views/ArenaEventView.vue'),
    modern: () => import('@features/events/views/ModernEventView.vue'),
  },
  home: {
    minimal: () => import('@features/collection/views/HomeView.vue'),
    modern: () => import('@features/collection/views/ModernHomeView.vue'),
  },
  rewards: {
    minimal: () => import('@features/rewards/views/ArenaRewardsView.vue'),
    arena: () => import('@features/rewards/views/ArenaRewardsView.vue'),
    modern: () => import('@features/rewards/views/ModernRewardsView.vue'),
  },
  panel: {
    minimal: () =>
      import('@features/billing/views/SubscriptionDashboardView.vue'),
    arena: () => import('@features/profile/views/ArenaPanelView.vue'),
    modern: () => import('@features/profile/views/ModernPanelView.vue'),
  },
  plans: {
    minimal: () => import('@features/billing/views/SubscriptionsView.vue'),
    modern: () => import('@features/billing/views/ModernPlansView.vue'),
  },
};

export const themesImplementedFor = (section: ThemedSection): LauncherTheme[] =>
  (Object.keys(SECTION_VIEWS[section]) as LauncherTheme[]).filter(
    isLauncherTheme,
  );

export const resolveSectionView = (
  section: ThemedSection,
  theme: LauncherTheme,
): SectionLoader => {
  const implemented = isLauncherTheme(theme)
    ? SECTION_VIEWS[section][theme]
    : undefined;
  return implemented ?? SECTION_VIEWS[section].minimal;
};
